/* Arivon product page. Loaded only when the main-product section has "Enable Arivon product page" on.
   - Size guide drawer: opens from the "Size guide" link; closes with X, Esc, a tap outside and the
     browser Back button (a history entry is pushed on open). Focus returns to the link on close.
   - Mobile gallery dots (replace Dawn's "1 / 4" counter under 750px).
   - Trust line items open the Shipping & returns dropdown. */
(() => {
  if (window.arivonProductLoaded) return;
  window.arivonProductLoaded = true;

  /* ---------- Size guide drawer ---------- */
  let openDrawer = null;
  let openerId = null;
  let pushedState = false;

  const lockScroll = (lock) => document.documentElement.classList.toggle('arivon-scroll-lock', lock);

  const restoreFocus = () => {
    if (!openerId) return;
    // The variant picker re-renders on every option change, so look the link up again.
    const opener = document.querySelector(`[data-arivon-size-guide-open="${openerId}"]`);
    opener?.focus();
  };

  const finishClose = () => {
    if (!openDrawer) return;
    const drawer = openDrawer;
    openDrawer = null;
    if (drawer.open) drawer.close();
    lockScroll(false);
    restoreFocus();
  };

  // Close from the UI (X, Esc, backdrop): go back so the pushed history entry is consumed.
  const requestClose = () => {
    if (!openDrawer) return;
    if (pushedState) {
      pushedState = false;
      history.back();
      // popstate closes the drawer; fall back in case it doesn't fire.
      window.setTimeout(finishClose, 400);
    } else {
      finishClose();
    }
  };

  const openSizeGuide = (drawerId) => {
    const drawer = document.getElementById(drawerId);
    if (!drawer || typeof drawer.showModal !== 'function' || drawer.open) return;
    openDrawer = drawer;
    openerId = drawerId;
    drawer.showModal();
    lockScroll(true);
    drawer.querySelector('[data-arivon-drawer-close]')?.focus();
    if (!window.Shopify?.designMode) {
      history.pushState({ arivonDrawer: drawerId }, '');
      pushedState = true;
    }
  };

  window.addEventListener('popstate', () => {
    if (!openDrawer) return;
    pushedState = false;
    finishClose();
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-arivon-size-guide-open]');
    if (opener) {
      event.preventDefault();
      openSizeGuide(opener.dataset.arivonSizeGuideOpen);
      return;
    }

    if (!openDrawer) return;
    if (event.target.closest('[data-arivon-drawer-close]')) {
      requestClose();
      return;
    }
    // A click on the <dialog> itself (not its content) is a click on the backdrop.
    if (event.target === openDrawer) requestClose();
  });

  // Esc fires "cancel" on a modal <dialog>; route it through requestClose so history stays in sync.
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target !== openDrawer) return;
      event.preventDefault();
      requestClose();
    },
    true
  );

  // Some browsers close a modal <dialog> without "cancel" (e.g. a second Esc). Clean up either way.
  document.addEventListener(
    'close',
    (event) => {
      if (event.target !== openDrawer) return;
      if (pushedState) {
        pushedState = false;
        history.back();
      }
      finishClose();
    },
    true
  );

  /* ---------- Trust line -> Shipping & returns dropdown ---------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-arivon-open-details]');
    if (!link) return;
    const details = document.getElementById(link.dataset.arivonOpenDetails);
    if (!details) return;
    event.preventDefault();
    details.open = true;
    details.scrollIntoView({ behavior: 'smooth', block: 'start' });
    details.querySelector('summary')?.focus({ preventScroll: true });
  });

  /* ---------- Mobile gallery dots ---------- */
  const initDots = (productInfo) => {
    const viewer = productInfo.querySelector('[id^="GalleryViewer-"]');
    const list = viewer?.querySelector('.product__media-list');
    const buttons = viewer?.querySelector('.slider-buttons');
    if (!viewer || !list || !buttons || buttons.querySelector('.arivon-dots')) return;

    const slides = () => Array.from(list.querySelectorAll('li[data-media-id]'));
    if (slides().length < 2) return;

    const label = productInfo.dataset.arivonDotLabel || '[index]';
    const dots = document.createElement('div');
    dots.className = 'arivon-dots';
    buttons.querySelector('.slider-counter')?.after(dots);

    const currentIndex = () => {
      const items = slides();
      if (items.length < 2) return 0;
      const step = items[1].offsetLeft - items[0].offsetLeft || 1;
      return Math.min(items.length - 1, Math.max(0, Math.round(list.scrollLeft / step)));
    };

    const render = () => {
      const items = slides();
      if (dots.childElementCount !== items.length) {
        dots.innerHTML = '';
        items.forEach((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'arivon-dot';
          dot.setAttribute('aria-label', label.replace('[index]', index + 1));
          dot.addEventListener('click', () => {
            const target = slides()[index];
            if (target) list.scrollTo({ left: target.offsetLeft - slides()[0].offsetLeft, behavior: 'smooth' });
          });
          dots.appendChild(dot);
        });
      }
      const active = currentIndex();
      Array.from(dots.children).forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    let frame = null;
    list.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          render();
        });
      },
      { passive: true }
    );
    // Gallery items are re-ordered when a colour is picked.
    new MutationObserver(render).observe(list, { childList: true });
    render();
  };

  const initAll = () => document.querySelectorAll('product-info.arivon-pdp').forEach(initDots);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
